import React, { useState } from 'react';
import SpinningSun from '../../../../common/SpinningSun';
import './SlotManager.css';

const SlotManager = ({ 
  slots = [], 
  deviceSerial, 
  sunscreenTypes = [], 
  onRefillSlot, 
  onToggleSlot,
  isLoading = false 
}) => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [refillModalOpen, setRefillModalOpen] = useState(false);
  const [pendingRefills, setPendingRefills] = useState({});

  const getSPFClass = (spf) => {
    if (spf >= 50) return 'spf-high';
    if (spf >= 30) return 'spf-medium';
    return 'spf-low';
  };

  const getFillClass = (fillLevel) => {
    if (fillLevel >= 80) return 'fill-excellent';
    if (fillLevel >= 50) return 'fill-good';
    if (fillLevel >= 20) return 'fill-warning';
    return 'fill-critical';
  };

  const getFillIcon = (fillLevel) => {
    if (fillLevel >= 80) return '🟢';
    if (fillLevel >= 50) return '🟡';
    if (fillLevel >= 20) return '🟠';
    return '🔴';
  };

  const handleRefillClick = (slotIndex) => {
    setSelectedSlot(slotIndex);
    setRefillModalOpen(true);
  };

  const handleRefillSubmit = (refillData) => {
    if (selectedSlot !== null) {
      onRefillSlot?.(selectedSlot, refillData);
      setRefillModalOpen(false);
      setSelectedSlot(null);
    }
  };

  const handleToggleSlot = (slotIndex) => {
    onToggleSlot?.(slotIndex);
  };

  // Dropdown handlers
  const handleSunscreenTypeChange = (slotIndex, sunscreenTypeId) => {
    const sunscreenType = sunscreenTypes.find(type => type.id === sunscreenTypeId);
    if (sunscreenType) {
      setPendingRefills(prev => ({
        ...prev,
        [slotIndex]: {
          sunscreenType: sunscreenType,
          volume: 1500 // Default volume
        }
      }));
    }
  };

  const handleVolumeChange = (slotIndex, volume) => {
    setPendingRefills(prev => ({
      ...prev,
      [slotIndex]: {
        ...prev[slotIndex],
        volume: parseInt(volume)
      }
    }));
  };

  const handleConfirmRefill = (slotIndex) => {
    const refillData = pendingRefills[slotIndex];
    if (refillData) {
      onRefillSlot?.(slotIndex, {
        sunscreen_type: refillData.sunscreenType.id,
        volume_ml: refillData.volume,
        slot_index: slotIndex
      });
      setPendingRefills(prev => {
        const newPending = { ...prev };
        delete newPending[slotIndex];
        return newPending;
      });
    }
  };

  const handleCancelRefill = (slotIndex) => {
    setPendingRefills(prev => {
      const newPending = { ...prev };
      delete newPending[slotIndex];
      return newPending;
    });
  };

  return (
    <div className="slot-manager">
      <div className="slot-manager-header">
        <h3 className="slot-manager-title">Sunscreen Dispensers</h3>
        <div className="slot-manager-summary">
          <span className="slots-count">{slots.length} dispensers</span>
          <span className="slots-active">
            {slots.filter(slot => slot.status === 'active').length} ready to use
          </span>
        </div>
      </div>

      {/* Sunscreen Types Reference */}
      <div className="sunscreen-reference">
        <h4 className="reference-title">Available Sunscreen Options</h4>
        <div className="reference-grid">
          {sunscreenTypes.map((sunscreenType) => (
            <div
              key={sunscreenType.id}
              className="sunscreen-reference-item"
              style={{ backgroundColor: sunscreenType.color }}
            >
              <div className="sunscreen-icon">{sunscreenType.icon}</div>
              <div className="sunscreen-info">
                <div className="sunscreen-name">{sunscreenType.name}</div>
                <div className="sunscreen-spf">SPF {sunscreenType.spf}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="slots-grid">
        {slots.map((slot, index) => (
          <div 
            key={index} 
            className={`slot-card ${slot.status || 'disabled'} ${isLoading ? 'loading' : ''}`}
          >
            {/* Slot Header */}
            <div className="slot-card-header">
              <div className="slot-card-title-section">
                <h4 className="slot-card-title">Slot {index + 1}</h4>
                <div className={`slot-status-badge ${slot.status || 'disabled'}`}>
                  {slot.status === 'active' ? 'Active' : 'Disabled'}
                </div>
              </div>
              <div className="slot-card-actions">
                <button
                  className="slot-toggle-btn"
                  onClick={() => handleToggleSlot(index)}
                  disabled={isLoading}
                  aria-label={`Toggle slot ${index + 1}`}
                >
                  {slot.status === 'active' ? '⏸️' : '▶️'}
                </button>
              </div>
            </div>

            {/* Slot Content */}
            {slot.status === 'active' ? (
              <div className="slot-card-content">
                {/* Sunscreen Type */}
                <div className="slot-info-section">
                  <div className="slot-info-item">
                    <div className="slot-info-label">Sunscreen Type</div>
                    <div className="slot-info-value">
                      <span className={`spf-badge ${getSPFClass(slot.spf)}`}>
                        SPF {slot.spf}
                      </span>
                      <span className="sunscreen-brand">{slot.brand}</span>
                    </div>
                  </div>

                  <div className="slot-info-item">
                    <div className="slot-info-label">Type</div>
                    <div className="slot-info-value">{slot.type}</div>
                  </div>

                  <div className="slot-info-item">
                    <div className="slot-info-label">Volume</div>
                    <div className="slot-info-value">{slot.volume_ml}ml</div>
                  </div>
                </div>

                {/* Fill Level */}
                <div className="slot-fill-section">
                  <div className="fill-header">
                    <div className="fill-label">Fill Level</div>
                    <div className="fill-status">
                      <span className="fill-icon">{getFillIcon(slot.fill_level)}</span>
                      <span className="fill-percentage">{slot.fill_level}%</span>
                    </div>
                  </div>
                  <div className="progress-bar-container">
                    <div className="progress-bar">
                      <div 
                        className={`progress-bar-fill ${getFillClass(slot.fill_level)}`}
                        style={{ width: `${slot.fill_level}%` }}
                      />
                    </div>
                  </div>
                  <div className="fill-details">
                    <span className="fill-current">{Math.round(slot.fill_level * slot.volume_ml / 100)}ml</span>
                    <span className="fill-max">{slot.volume_ml}ml</span>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="slot-usage-section">
                  <div className="usage-stats">
                    <div className="usage-stat">
                      <div className="usage-icon">📊</div>
                      <div className="usage-content">
                        <div className="usage-label">Dispenses</div>
                        <div className="usage-value">{slot.dispenses || 0}</div>
                      </div>
                    </div>
                    <div className="usage-stat">
                      <div className="usage-icon">📅</div>
                      <div className="usage-content">
                        <div className="usage-label">Last Refill</div>
                        <div className="usage-value">
                          {slot.last_refill 
                            ? new Date(slot.last_refill).toLocaleDateString()
                            : 'Never'
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Refill Controls */}
                <div className="slot-refill-section">
                  <div className="refill-controls">
                    <div className="refill-selector">
                      <label className="refill-label">Sunscreen Type:</label>
                      <select
                        className="refill-dropdown"
                        value={pendingRefills[index]?.sunscreenType?.id || ''}
                        onChange={(e) => handleSunscreenTypeChange(index, e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="">Choose sunscreen...</option>
                        {sunscreenTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name} (SPF {type.spf})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="refill-selector">
                      <label className="refill-label">Amount to Add:</label>
                      <input
                        type="number"
                        min="100"
                        max="5000"
                        step="1"
                        value={pendingRefills[index]?.volume || 1500}
                        onChange={(e) => handleVolumeChange(index, e.target.value)}
                        disabled={isLoading || !pendingRefills[index]}
                        className="refill-dropdown"
                        placeholder="Enter amount in ml (e.g. 1500)"
                      />
                      <small className="input-hint">Enter amount in milliliters (100-5000ml)</small>
                    </div>
                  </div>
                  
                  {pendingRefills[index] && (
                    <div className="refill-actions">
                      <button
                        className="refill-confirm-btn"
                        onClick={() => handleConfirmRefill(index)}
                        disabled={isLoading}
                      >
                        ✅ Add Sunscreen
                      </button>
                      <button
                        className="refill-cancel-btn"
                        onClick={() => handleCancelRefill(index)}
                        disabled={isLoading}
                      >
                        ❌ Cancel
                      </button>
                    </div>
                  )}
                  
                  {!pendingRefills[index] && (
                    <div className="slot-actions">
                      <button
                        className="slot-action-btn prime-btn"
                        onClick={() => {/* Handle prime pump */}}
                        disabled={isLoading}
                      >
                        <span className="btn-icon">💧</span>
                        <span className="btn-text">Test Flow</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="slot-card-empty">
                <div className="empty-icon">📭</div>
                <div className="empty-text">Slot Disabled</div>
                <div className="empty-subtext">
                  Enable this slot to add sunscreen
                </div>
                <button
                  className="enable-slot-btn"
                  onClick={() => handleToggleSlot(index)}
                  disabled={isLoading}
                >
                  Enable Slot
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Refill Modal */}
      {refillModalOpen && (
        <RefillModal
          slotIndex={selectedSlot}
          currentSlot={selectedSlot !== null ? slots[selectedSlot] : null}
          sunscreenTypes={sunscreenTypes}
          onSubmit={handleRefillSubmit}
          onClose={() => {
            setRefillModalOpen(false);
            setSelectedSlot(null);
          }}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

// Refill Modal Component
const RefillModal = ({ 
  slotIndex, 
  currentSlot, 
  sunscreenTypes, 
  onSubmit, 
  onClose, 
  isLoading 
}) => {
  const [selectedType, setSelectedType] = useState(currentSlot?.sunscreen_type || '');
  const [volume, setVolume] = useState(currentSlot?.volume_ml || 1500);
  const [customVolume, setCustomVolume] = useState('');
  const [useCustomVolume, setUseCustomVolume] = useState(false);

  const volumeOptions = [
    { label: '500ml', value: 500 },
    { label: '1000ml', value: 1000 },
    { label: '1500ml', value: 1500 },
    { label: '2000ml', value: 2000 },
    { label: 'Custom', value: 'custom' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const refillData = {
      sunscreen_type: selectedType,
      volume_ml: useCustomVolume ? parseInt(customVolume) : volume,
      slot_index: slotIndex
    };

    onSubmit(refillData);
  };

  const handleVolumeChange = (value) => {
    if (value === 'custom') {
      setUseCustomVolume(true);
      setVolume(0);
    } else {
      setUseCustomVolume(false);
      setVolume(value);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Refill Slot {slotIndex + 1}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="refill-form-grid">
            {/* Sunscreen Type Selection */}
            <div className="form-group">
              <label className="form-label">Sunscreen Type</label>
              <select
                className="form-control"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                required
              >
                <option value="">Select sunscreen type</option>
                {sunscreenTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.brand} - SPF {type.spf} ({type.type})
                  </option>
                ))}
              </select>
            </div>

            {/* Volume Selection */}
            <div className="form-group">
              <label className="form-label">Refill Volume</label>
              <div className="volume-options">
                {volumeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`volume-option ${
                      (useCustomVolume && option.value === 'custom') ||
                      (!useCustomVolume && volume === option.value)
                        ? 'selected'
                        : ''
                    }`}
                    onClick={() => handleVolumeChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {useCustomVolume && (
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter custom volume (ml)"
                  value={customVolume}
                  onChange={(e) => setCustomVolume(e.target.value)}
                  min="1"
                  max="5000"
                  required
                />
              )}
            </div>

            {/* Current Slot Info */}
            {currentSlot && (
              <div className="current-slot-info">
                <h4>Current Slot Status</h4>
                <div className="slot-info-grid">
                  <div className="info-item">
                    <span className="info-label">Current Type:</span>
                    <span className="info-value">{currentSlot.brand} SPF {currentSlot.spf}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Current Fill:</span>
                    <span className="info-value">{currentSlot.fill_level}%</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Volume:</span>
                    <span className="info-value">{currentSlot.volume_ml}ml</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isLoading || !selectedType || (!useCustomVolume && !volume) || (useCustomVolume && !customVolume)}
          >
            {isLoading ? (
              <>
                <SpinningSun size="small" />
                Refilling...
              </>
            ) : (
              <>
                <span className="btn-icon">🔄</span>
                Start Refill
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlotManager;
